import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  Sparkles,
  ChevronDown,
  Wrench,
  MessageSquare,
  Palette,
  Shield,
  ListOrdered,
  Wand2,
  type LucideIcon
} from 'lucide-react'
import type { InfographicStyle } from '../../stores/infographicStore'

interface StyleOption {
  value: InfographicStyle
  label: string
  description: string
  icon: LucideIcon
}

const STYLE_OPTIONS: StyleOption[] = [
  {
    value: 'more-technical',
    label: 'More Technical',
    description: 'Add technical details and specifications',
    icon: Wrench
  },
  {
    value: 'simpler-language',
    label: 'Simpler Language',
    description: 'Use everyday words and shorter sentences',
    icon: MessageSquare
  },
  {
    value: 'visual-focus',
    label: 'Visual Focus',
    description: 'Emphasize visual elements and icons',
    icon: Palette
  },
  {
    value: 'warranty-emphasis',
    label: 'Warranty Emphasis',
    description: 'Highlight warranty and support options',
    icon: Shield
  },
  {
    value: 'step-by-step',
    label: 'Step-by-Step',
    description: 'Break down into numbered instructions',
    icon: ListOrdered
  },
  {
    value: 'surprise-me',
    label: 'Surprise Me!',
    description: 'Let AI choose the best creative approach',
    icon: Wand2
  }
]

interface StyleChooserDropdownProps {
  onSelectStyle: (style: InfographicStyle) => void
  disabled?: boolean
}

export default function StyleChooserDropdown({
  onSelectStyle,
  disabled
}: StyleChooserDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const wrapperRef = useRef<HTMLDivElement>(null)
  const dropdownMenuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const clickedButton = buttonRef.current?.contains(target)
      const clickedMenu = dropdownMenuRef.current?.contains(target)

      if (!clickedButton && !clickedMenu) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSelectStyle = (style: InfographicStyle) => {
    onSelectStyle(style)
    setIsOpen(false)
  }

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left
      })
    }
    setIsOpen(!isOpen)
  }

  return (
    <div ref={wrapperRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className="btn btn-ghost"
        aria-label="Choose regeneration style"
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-haspopup="true"
      >
        <Sparkles className="w-4 h-4" />
        Regenerate
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownMenuRef}
          className="fixed w-72 bg-[var(--bbs-glass-card)] backdrop-blur-xl border border-[var(--bbs-color-border)] rounded-xl shadow-[var(--shadow-glass-elevated)] overflow-hidden"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            zIndex: 9999
          }}
        >
          <div className="p-2 space-y-1">
            {STYLE_OPTIONS.map((option) => {
              const Icon = option.icon
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelectStyle(option.value)}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <Icon className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--bbs-color-accent)]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[var(--bbs-text-primary)] group-hover:text-[var(--bbs-color-accent)] transition-colors">
                        {option.label}
                      </div>
                      <div className="text-xs text-[var(--bbs-text-tertiary)] mt-0.5">
                        {option.description}
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
